import { useMemo, useCallback } from 'react'
import { POST_REGISTRATION_STATUSES } from '../../../../constants/leagueStatuses'
import { isAdmin as isAdminRole } from '../../../../utils/userRoles'
import {
  RESTRICTED_FIELDS,
  RESTRICTED_FIELDS_FOR_CHANGE_DETECTION,
} from '../leagueFormConstants'

const DATE_FIELDS = [
  'registration_start',
  'registration_end',
  'play_start',
  'play_end',
]

export default function useLeagueEditRestrictions({
  isEditMode,
  leagueStatus,
  user,
  originalLeagueData,
}) {
  const restrictionState = useMemo(() => {
    if (!isEditMode || !leagueStatus || !user) {
      return {
        restrictedFields: [],
        isLeagueStarted: false,
        userCanEdit: true,
        requiresConfirmation: false,
      }
    }

    const isLeagueStarted = POST_REGISTRATION_STATUSES.includes(leagueStatus)
    const userCanEdit = isAdminRole(user)

    if (!userCanEdit) {
      return {
        restrictedFields: [],
        isLeagueStarted,
        userCanEdit: false,
        requiresConfirmation: false,
      }
    }

    if (isLeagueStarted && user.user_type === 'admin') {
      return {
        restrictedFields: RESTRICTED_FIELDS,
        isLeagueStarted,
        userCanEdit,
        requiresConfirmation: false,
      }
    }

    return {
      restrictedFields: [],
      isLeagueStarted,
      userCanEdit,
      requiresConfirmation: isLeagueStarted && user.user_type === 'owner',
    }
  }, [isEditMode, leagueStatus, user])

  const isFieldDisabled = useCallback(
    (fieldName) => restrictionState.restrictedFields.includes(fieldName),
    [restrictionState],
  )

  const hasRestrictedChanges = useCallback(
    (submitData) => {
      if (
        !restrictionState.isLeagueStarted ||
        !restrictionState.requiresConfirmation ||
        !originalLeagueData
      ) {
        return false
      }

      return RESTRICTED_FIELDS_FOR_CHANGE_DETECTION.some((field) => {
        if (submitData[field] === undefined) return false

        if (DATE_FIELDS.includes(field)) {
          const submitValue = submitData[field]
          const originalValue = originalLeagueData[field]
            ? new Date(originalLeagueData[field]).toISOString()
            : null
          return submitValue !== originalValue
        }

        return submitData[field] !== originalLeagueData[field]
      })
    },
    [restrictionState, originalLeagueData],
  )

  return {
    ...restrictionState,
    isFieldDisabled,
    hasRestrictedChanges,
  }
}
